'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { IconRefresh, IconUpload, IconX } from '@tabler/icons-react';
import { ImageIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import { computeFileMD5 } from '@/shared/lib/client-hash';
import { cn } from '@/shared/lib/utils';
import { ImageZoomButton } from './image-zoom-button';
export type UploadStatus = 'idle' | 'uploading' | 'uploaded' | 'error';
export interface ImageUploaderValue {
  id: string;
  preview: string;
  url?: string;
  status: UploadStatus;
  size?: number;
}
interface ImageUploaderProps {
  allowMultiple?: boolean;
  maxImages?: number;
  maxSizeMB?: number;
  maxDimension?: number;
  title?: string;
  emptyHint?: string;
  className?: string;
  defaultPreviews?: string[];
  onChange?: (items: ImageUploaderValue[]) => void;
}
interface UploadItem extends ImageUploaderValue {
  file?: File;
  uploadKey?: string;
}
const formatBytes = (bytes?: number) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(2)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(2)} MB`;
};
const getImageDimensions = (file: File): Promise<{
  width: number;
  height: number;
}> => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
};
const uploadImageFile = async (file: File) => {
  const md5 = await computeFileMD5(file);
  const res = await fetch('/api/storage/presign', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      md5,
      contentType: file.type
    })
  });
  if (!res.ok) {
    throw new Error(`Presign failed with status ${res.status}`);
  }
  const result = await res.json();
  if (result.code !== 0 || !result.data) {
    throw new Error(result.message || 'Presign failed');
  }
  const {
    data
  } = result;
  if (data.deduplicated) {
    return data.publicUrl as string;
  }
  const putRes = await fetch(data.presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type
    }
  });
  if (!putRes.ok) {
    throw new Error(`Upload to R2 failed with status ${putRes.status}`);
  }
  return data.publicUrl as string;
};
export function ImageUploader({
  allowMultiple = false,
  maxImages = 1,
  maxSizeMB = 10,
  maxDimension,
  title,
  emptyHint,
  className,
  defaultPreviews,
  onChange
}: ImageUploaderProps) {
  const t = useTranslations('common.uploader');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isInitializedRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const isInternalChangeRef = useRef(false);
  const replaceTargetIdRef = useRef<string | null>(null);
  const dragCounterRef = useRef(0);
  const [isDragActive, setIsDragActive] = useState(false);

  // 使用 defaultPreviews 初始化 items，只在组件挂载时执行一次
  const [items, setItems] = useState<UploadItem[]>(() => {
    if (defaultPreviews?.length) {
      return defaultPreviews.map((url, index) => ({
        id: `preset-${url}-${index}`,
        preview: url,
        url,
        status: 'uploaded' as UploadStatus
      }));
    }
    return [];
  });
  const maxCount = allowMultiple ? maxImages : 1;
  const maxBytes = maxSizeMB * 1024 * 1024;

  // 更新 onChange ref
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // 同步 defaultPreviews 的变化（只在外部变化时同步，避免循环）
  useEffect(() => {
    // 跳过初始化
    if (!isInitializedRef.current) {
      return;
    }

    // 如果是内部变化触发的，跳过
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }
    const defaultUrls = defaultPreviews || [];

    // 使用函数式更新来访问最新的 items
    setItems(currentItems => {
      const currentUrls = currentItems.filter(item => item.status === 'uploaded' && item.url).map(item_0 => item_0.url as string);

      // 比较当前 items 和 defaultPreviews 是否一致
      const isSame = defaultUrls.length === currentUrls.length && defaultUrls.every((url_0, index_0) => url_0 === currentUrls[index_0]);

      // 只有当不一致时才返回新的 items
      if (!isSame) {
        return defaultUrls.map((url_1, index_1) => ({
          id: `preset-${url_1}-${index_1}`,
          preview: url_1,
          url: url_1,
          status: 'uploaded' as UploadStatus
        }));
      }
      return currentItems;
    });
  }, [defaultPreviews]);

  // 清理 blob URLs
  useEffect(() => {
    return () => {
      items.forEach(item_1 => {
        if (item_1.preview.startsWith('blob:')) {
          URL.revokeObjectURL(item_1.preview);
        }
      });
    };
  }, [items]);

  // 当 items 变化时触发 onChange，但跳过初始化时的调用
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      return;
    }

    // 标记这是内部变化
    isInternalChangeRef.current = true;
    onChangeRef.current?.(items.map(({
      id,
      preview,
      url: url_2,
      status,
      size
    }) => ({
      id,
      preview,
      url: url_2,
      status,
      size
    })));
  }, [items]);
  const replaceItems = (pairs: Array<{
    id: string;
    file: File;
  }>) => {
    pairs.forEach(({
      id: id_0,
      file
    }) => {
      const uploadKey = `${Date.now()}-${Math.random()}`;
      const nextPreview = URL.createObjectURL(file);
      setItems(prev => prev.map(item_2 => {
        if (item_2.id !== id_0) return item_2;
        if (item_2.preview.startsWith('blob:')) {
          URL.revokeObjectURL(item_2.preview);
        }
        return {
          ...item_2,
          preview: nextPreview,
          file,
          size: file.size,
          url: undefined,
          status: 'uploading' as UploadStatus,
          uploadKey
        };
      }));
      uploadImageFile(file).then(url_3 => {
        setItems(prev_0 => prev_0.map(item_3 => {
          if (item_3.id !== id_0) return item_3;
          if (item_3.uploadKey !== uploadKey) return item_3; // stale upload
          if (item_3.preview.startsWith('blob:')) {
            URL.revokeObjectURL(item_3.preview);
          }
          return {
            ...item_3,
            preview: url_3,
            url: url_3,
            status: 'uploaded' as UploadStatus,
            file: undefined
          };
        }));
      }).catch((error: any) => {
        console.error('Upload failed:', error);
        toast.error(error?.message ? t('upload_failed_with_reason', {
          reason: error.message
        }) : t('upload_failed'));
        setItems(prev_1 => prev_1.map(item_4 => {
          if (item_4.id !== id_0) return item_4;
          if (item_4.uploadKey !== uploadKey) return item_4; // stale upload
          return {
            ...item_4,
            status: 'error' as UploadStatus
          };
        }));
      }).finally(() => {
        if (inputRef.current) inputRef.current.value = '';
      });
    });
  };
  const validateFile = async (file_0: File): Promise<boolean> => {
    const validTypes = ['image/jpeg', 'image/png'];
    if (!validTypes.includes(file_0.type)) {
      toast.error(t('image_format_unsupported', {
        fileName: file_0.name
      }));
      return false;
    }
    if (file_0.size > maxBytes) {
      toast.error(t('image_exceeds_size', {
        fileName: file_0.name,
        maxSizeMB
      }));
      return false;
    }
    if (maxDimension) {
      try {
        const {
          width,
          height
        } = await getImageDimensions(file_0);
        if (width > maxDimension || height > maxDimension) {
          toast.error(t('image_exceeds_dimension', {
            fileName: file_0.name,
            width,
            height,
            maxDimension
          }));
          return false;
        }
      } catch {
        toast.error(t('image_could_not_read', {
          fileName: file_0.name
        }));
        return false;
      }
    }
    return true;
  };
  const handleFiles = async (selectedFiles: File[]) => {
    const replaceTargetId = replaceTargetIdRef.current;
    if (replaceTargetId) {
      // reset immediately to avoid sticky replace mode
      replaceTargetIdRef.current = null;
      const file_1 = selectedFiles[0];
      if (!file_1) return;
      const isValid = await validateFile(file_1);
      if (!isValid) {
        if (inputRef.current) inputRef.current.value = '';
        return;
      }
      replaceItems([{
        id: replaceTargetId,
        file: file_1
      }]);
      return;
    }
    const availableSlots = maxCount - items.length;
    const validated = await Promise.all(selectedFiles.map(async file_2 => ({
      file: file_2,
      valid: await validateFile(file_2)
    })));
    const filesToAdd = validated.filter(({
      valid
    }) => valid).map(({
      file: file_3
    }) => file_3).slice(0, Math.max(availableSlots, 0));
    if (!filesToAdd.length) {
      // when full: replace from the end backwards
      if (items.length) {
        const normalized = validated.filter(({
          valid: valid_0
        }) => valid_0).map(({
          file: file_4
        }) => file_4);
        if (!normalized.length) return;
        const k = Math.min(normalized.length, items.length);
        const tail = items.slice(-k);
        const pairs_0: Array<{
          id: string;
          file: File;
        }> = [];
        for (let i = 0; i < k; i += 1) {
          const targetId = tail[tail.length - 1 - i]?.id;
          const file_5 = normalized[i];
          if (targetId && file_5) pairs_0.push({
            id: targetId,
            file: file_5
          });
        }
        if (pairs_0.length) {
          replaceItems(pairs_0);
        }
      }
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    if (availableSlots < selectedFiles.length) {
      toast.message(t('image_limit_hint', {
        count: filesToAdd.length
      }));
    }
    const newItems = filesToAdd.map(file_6 => ({
      id: `${file_6.name}-${file_6.lastModified}-${Math.random()}`,
      preview: URL.createObjectURL(file_6),
      file: file_6,
      size: file_6.size,
      status: 'uploading' as UploadStatus,
      uploadKey: `${Date.now()}-${Math.random()}`
    }));
    setItems(prev_2 => [...prev_2, ...newItems]);

    // Upload in parallel
    Promise.all(newItems.map(async item_5 => {
      try {
        const url_4 = await uploadImageFile(item_5.file as File);
        setItems(prev_4 => {
          const next_0 = prev_4.map(current_0 => {
            if (current_0.id === item_5.id) {
              if (current_0.uploadKey && item_5.uploadKey) {
                if (current_0.uploadKey !== item_5.uploadKey) return current_0; // stale upload
              }
              // Revoke the blob URL since we have the uploaded URL now
              if (current_0.preview.startsWith('blob:')) {
                URL.revokeObjectURL(current_0.preview);
              }
              return {
                ...current_0,
                preview: url_4,
                // Replace preview with uploaded URL
                url: url_4,
                status: 'uploaded' as UploadStatus,
                file: undefined
              };
            }
            return current_0;
          });
          return next_0;
        });
      } catch (error_0: any) {
        console.error('Upload failed:', error_0);
        toast.error(error_0?.message ? t('upload_failed_with_reason', {
          reason: error_0.message
        }) : t('upload_failed'));
        setItems(prev_3 => {
          const next = prev_3.map(current => {
            if (current.id !== item_5.id) return current;
            if (current.uploadKey && current.uploadKey !== item_5.uploadKey) {
              return current; // stale upload
            }
            return {
              ...current,
              status: 'error' as UploadStatus
            };
          });
          return next;
        });
      }
    }));
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };
  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles_0 = Array.from(event.target.files || []);
    if (!selectedFiles_0.length) return;
    handleFiles(selectedFiles_0);
  };
  const handlePaste = (event_0: React.ClipboardEvent<HTMLDivElement>) => {
    const clipboardItems = Array.from(event_0.clipboardData?.items || []);
    const validPasteTypes = ['image/jpeg', 'image/png'];
    const files = clipboardItems.filter(item_6 => item_6.kind === 'file' && validPasteTypes.includes(item_6.type)).map(item_7 => item_7.getAsFile()).filter(Boolean) as File[];
    if (!files.length) return;
    event_0.preventDefault();
    handleFiles(files);
  };
  const handleDragEnter = (event_1: React.DragEvent<HTMLDivElement>) => {
    event_1.preventDefault();
    event_1.stopPropagation();
    dragCounterRef.current += 1;
    setIsDragActive(true);
  };
  const handleDragOver = (event_2: React.DragEvent<HTMLDivElement>) => {
    event_2.preventDefault();
    event_2.stopPropagation();
    event_2.dataTransfer.dropEffect = 'copy';
    if (!isDragActive) setIsDragActive(true);
  };
  const handleDragLeave = (event_3: React.DragEvent<HTMLDivElement>) => {
    event_3.preventDefault();
    event_3.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragActive(false);
    }
  };
  const handleDrop = (event_4: React.DragEvent<HTMLDivElement>) => {
    event_4.preventDefault();
    event_4.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragActive(false);
    const validDropTypes = ['image/jpeg', 'image/png'];
    const files_0 = Array.from(event_4.dataTransfer?.files || []).filter(file_7 => validDropTypes.includes(file_7.type));
    if (!files_0.length) return;
    handleFiles(files_0);
  };
  const handleRemove = (id_1: string) => {
    setItems(prev_5 => {
      const next_1 = prev_5.filter(item_8 => item_8.id !== id_1);
      const removed = prev_5.find(item_9 => item_9.id === id_1);
      if (removed?.preview.startsWith('blob:')) {
        URL.revokeObjectURL(removed.preview);
      }
      return next_1;
    });
  };
  const openFilePicker = () => {
    inputRef.current?.click();
  };
  const openReplacePicker = (id_2: string) => {
    replaceTargetIdRef.current = id_2;
    openFilePicker();
  };
  const countLabel = useMemo(() => `${items.length}/${maxCount}`, [items.length, maxCount]);
  return <div className={cn('focus-visible:ring-primary/50 relative focus:outline-none focus-visible:ring-2 focus-visible:ring-inset', isDragActive && 'ring-primary/70 ring-offset-background ring-2 ring-offset-2 ring-inset', className)} tabIndex={0} onPaste={handlePaste} onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
      {isDragActive && <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-black/10 backdrop-blur-sm">
          <div className="bg-background/80 text-foreground rounded-full px-4 py-2 text-sm font-medium shadow-sm">
            {t('drop_to_upload')}
          </div>
        </div>}
      <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" multiple={allowMultiple} onChange={handleSelect} className="hidden" />

      {title && <div className="text-foreground mb-2 flex items-center justify-between text-sm font-medium">
          <div className="flex items-center gap-2">
            <ImageIcon className="text-primary h-4 w-4" />
            <span>{title}</span>
            {/* <span className="text-primary text-xs">({countLabel})</span> */}
          </div>
        </div>}

      <div className={cn('flex flex-wrap gap-4', allowMultiple ? 'flex-wrap' : 'w-full flex-nowrap')}>
        {items.map(item_10 => <div key={item_10.id} className={cn('group border-border bg-muted/50 hover:border-border hover:bg-muted relative overflow-hidden rounded-xl border p-1 shadow-sm transition', allowMultiple ? 'h-32 w-32' : 'h-40', !allowMultiple && 'w-full')}>
            <div className="relative h-full overflow-hidden rounded-lg">
              <img src={item_10.preview} alt="Reference" className={cn('h-full w-full rounded-lg object-contain', allowMultiple ? 'w-32' : 'w-full')} />
              {item_10.size && <span className="bg-background text-muted-foreground absolute bottom-2 left-2 rounded-md px-2 py-1 text-[10px] font-medium">
                  {formatBytes(item_10.size)}
                </span>}
              {/* Zoom button — top left, same as video */}
              <ImageZoomButton src={item_10.url || item_10.preview} className="absolute top-2 left-2 z-20 h-6 w-6" />
              {item_10.status !== 'uploading' && <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/35 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
                  <Button type="button" size="icon" variant="secondary" className="bg-background/50 text-foreground hover:bg-background/50 h-10 w-10 rounded-full shadow-sm backdrop-blur focus-visible:ring-2 focus-visible:ring-white/70" onClick={() => openReplacePicker(item_10.id)} aria-label="Upload a new image to replace">
                    <IconRefresh className="h-5 w-5" />
                  </Button>
                </div>}
              {item_10.status === 'uploading' && <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 text-xs font-medium text-white">
                  {t('uploading')}
                </div>}
              {item_10.status === 'error' && <div className="absolute inset-0 z-10 flex items-center justify-center bg-red-500/70 text-xs font-medium text-white">
                  {t('failed')}
                </div>}
              <Button size="icon" variant="destructive" className="absolute top-2 right-2 z-20 h-7 w-7" onClick={() => handleRemove(item_10.id)} aria-label="Remove image">
                <IconX className="h-4 w-4" />
              </Button>
            </div>
          </div>)}

        {items.length < maxCount && <div className={cn('group border-border bg-muted/50 hover:border-border hover:bg-muted relative overflow-hidden rounded-xl border border-dashed p-1 shadow-sm transition', allowMultiple ? 'h-32 w-32' : 'h-40', !allowMultiple && 'w-full')}>
            <div className="relative h-full overflow-hidden rounded-lg">
              <button type="button" className={cn('focus-visible:ring-primary/50 flex h-full flex-col items-center justify-center gap-2 rounded-lg px-4 py-3 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset', allowMultiple ? 'w-32' : 'w-full')} onClick={openFilePicker}>
                <div className="border-border group-hover:border-primary flex h-10 w-10 items-center justify-center rounded-full border border-dashed transition-colors">
                  <IconUpload className="group-hover:text-primary h-5 w-5 transition-colors" />
                </div>
                <span className="text-xs font-medium">{t('upload')}</span>
                {title && emptyHint && <span className="text-muted-foreground line-clamp-2 text-center text-xs">
                    {emptyHint}
                  </span>}
              </button>
            </div>
          </div>}
      </div>

      {!title && <div className="text-muted-foreground text-xs">{emptyHint}</div>}
    </div>;
}